import { useCallback, useEffect, useRef, useState } from "react";
import type {
  TEmailBuilderActivePanel,
  TEmailBuilderEditingMode,
} from "redux/reducers/emailBuilder/emailBuilder";

/** Start nav close partway through panel exit (~half of the 500ms sheet transition). */
const MOBILE_NAV_CLOSE_OVERLAP_MS = 240;

interface IUseEmailBuilderMobileToolbarDrawerOptions {
  enabled: boolean;
  mode: "design" | "preview";
  activePanel: TEmailBuilderActivePanel;
  editingMode: TEmailBuilderEditingMode;
  onCloseToolbar: () => void;
  onClosePanel: () => void;
  onBeforeOpen: () => void;
  onSelectPanel: (panel: Exclude<TEmailBuilderActivePanel, "none">) => void;
}

export interface IEmailBuilderMobileToolbarDrawerState {
  toolbarOpen: boolean;
  panelOpen: boolean;
  toggleTools: () => void;
  closeToolbar: () => void;
  closePanel: () => void;
  dismissPanelThenToolbar: () => void;
  selectNavItem: (panel: Exclude<TEmailBuilderActivePanel, "none">) => void;
}

export function useEmailBuilderMobileToolbarDrawer(
  options: IUseEmailBuilderMobileToolbarDrawerOptions,
): IEmailBuilderMobileToolbarDrawerState {
  const {
    enabled,
    mode,
    activePanel,
    editingMode,
    onCloseToolbar,
    onClosePanel,
    onBeforeOpen,
    onSelectPanel,
  } = options;
  const [navOpenRequested, setNavOpenRequested] = useState(false);
  const navCloseAfterPanelTimerRef = useRef<number | null>(null);

  const panelOpen = activePanel !== "none";
  const navOpen =
    navOpenRequested &&
    enabled &&
    mode === "design" &&
    editingMode !== "block-settings";
  const toolbarOpen = enabled && mode === "design" && (navOpen || panelOpen);

  function clearNavCloseAfterPanelTimer() {
    if (navCloseAfterPanelTimerRef.current !== null) {
      window.clearTimeout(navCloseAfterPanelTimerRef.current);
      navCloseAfterPanelTimerRef.current = null;
    }
  }

  const closeToolbar = useCallback(() => {
    clearNavCloseAfterPanelTimer();
    setNavOpenRequested(false);
    onCloseToolbar();
  }, [onCloseToolbar]);

  const closePanel = useCallback(() => {
    onClosePanel();
  }, [onClosePanel]);

  const dismissPanelThenToolbar = useCallback(() => {
    clearNavCloseAfterPanelTimer();

    if (activePanel !== "none") {
      onClosePanel();
      navCloseAfterPanelTimerRef.current = window.setTimeout(() => {
        navCloseAfterPanelTimerRef.current = null;
        setNavOpenRequested(false);
      }, MOBILE_NAV_CLOSE_OVERLAP_MS);
      return;
    }

    closeToolbar();
  }, [activePanel, closeToolbar, onClosePanel]);

  const openNav = useCallback(() => {
    onBeforeOpen();
    setNavOpenRequested(true);
  }, [onBeforeOpen]);

  const toggleTools = useCallback(() => {
    if (toolbarOpen) {
      closeToolbar();
      return;
    }

    openNav();
  }, [closeToolbar, openNav, toolbarOpen]);

  const selectNavItem = useCallback(
    (panel: Exclude<TEmailBuilderActivePanel, "none">) => {
      clearNavCloseAfterPanelTimer();
      onBeforeOpen();
      setNavOpenRequested(true);
      onSelectPanel(panel);
    },
    [onBeforeOpen, onSelectPanel],
  );

  useEffect(() => {
    return () => {
      clearNavCloseAfterPanelTimer();
    };
  }, []);

  useEffect(() => {
    if (
      !enabled ||
      mode !== "design" ||
      editingMode !== "block-settings" ||
      !panelOpen
    ) {
      return;
    }

    onClosePanel();
  }, [editingMode, enabled, mode, onClosePanel, panelOpen]);

  return {
    toolbarOpen,
    panelOpen,
    toggleTools,
    closeToolbar,
    closePanel,
    dismissPanelThenToolbar,
    selectNavItem,
  };
}
